import { useState, type KeyboardEvent } from 'react';

import './AssignmentsSearchInput.scss';

/**
 * The toolbar's assignment search box - a port of
 * `AssignmentsSearchInputComponent`.
 *
 * This kept `searchTerm` as a prop it also wrote to
 * locally, which made it half-controlled. Here it is fully controlled by the
 * shell (which keeps the term in the `q` query param), and only the transient
 * bits - focus, the highlighted suggestion - are local state.
 */

export interface AssignmentsSearchInputProps {
  searchTerm?: string;
  placeholder?: string;
  ariaLabel?: string;
  suggestions?: string[];
  onSearchChanged: (value: string) => void;
}

export function AssignmentsSearchInput({
  searchTerm = '',
  placeholder = 'Search assignments, tasks, clients, IDs...',
  ariaLabel = 'Search',
  suggestions = [],
  onSearchChanged
}: AssignmentsSearchInputProps) {
  const [isFocused, setIsFocused] = useState(false);
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(-1);

  const query = searchTerm.trim().toLowerCase();
  const filteredSuggestions = query
    ? suggestions.filter((suggestion) => suggestion.toLowerCase().includes(query)).slice(0, 8)
    : [];

  const showSuggestions = isFocused && filteredSuggestions.length > 0;

  const selectSuggestion = (value: string) => {
    setIsFocused(false);
    setActiveSuggestionIndex(-1);
    onSearchChanged(value);
  };

  const handleKeydown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (!filteredSuggestions.length) {
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveSuggestionIndex((index) => (index + 1) % filteredSuggestions.length);
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveSuggestionIndex((index) =>
        index <= 0 ? filteredSuggestions.length - 1 : index - 1
      );
      return;
    }

    if (event.key === 'Enter' && activeSuggestionIndex >= 0) {
      event.preventDefault();
      selectSuggestion(filteredSuggestions[activeSuggestionIndex]);
      return;
    }

    if (event.key === 'Escape') {
      setIsFocused(false);
      setActiveSuggestionIndex(-1);
    }
  };

  return (
    <div className="search-input">
      <span className="search-input__icon" aria-hidden="true">
        <svg viewBox="0 0 20 20">
          <circle cx="8.5" cy="8.5" r="6" />
          <line x1="13" y1="13" x2="18" y2="18" />
        </svg>
      </span>

      <input
        type="text"
        value={searchTerm}
        onChange={(event) => {
          setActiveSuggestionIndex(-1);
          onSearchChanged(event.target.value);
        }}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        onKeyDown={handleKeydown}
        placeholder={placeholder}
        aria-label={ariaLabel}
        aria-expanded={showSuggestions}
        aria-autocomplete="list"
        aria-controls="assignments-search-suggestions"
        role="combobox"
        autoComplete="off"
      />

      {showSuggestions && (
        <div
          id="assignments-search-suggestions"
          className="search-input__suggestions"
          role="listbox"
          onMouseLeave={() => setActiveSuggestionIndex(-1)}
        >
          {filteredSuggestions.map((suggestion, index) => (
            <button
              key={suggestion}
              type="button"
              className={[
                'search-input__suggestion',
                index === activeSuggestionIndex ? 'search-input__suggestion--active' : null
              ]
                .filter(Boolean)
                .join(' ')}
              role="option"
              aria-selected={index === activeSuggestionIndex}
              onMouseEnter={() => setActiveSuggestionIndex(index)}
              /* Keeps the input focused so `onBlur` does not tear the list down
                 before the click lands. */
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => selectSuggestion(suggestion)}
            >
              {suggestion}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default AssignmentsSearchInput;
